import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, OnDestroy, Injector } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { SinalizationService } from '@services/sinalization.service';
import { Store, select } from '@ngrx/store';
import { AppState } from '@store/state/app.state';
import { MarketDetails } from '@models/marketDetail';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil, take } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { getMarketDetails, getMarkets } from '@store/selectors/market.selector';
import { GetSinalization, GetNotification } from '@store/actions/user.action';
import { User, UserFlag } from '@models/user.model';
import { Markets } from '@models/markets';
import { getLoggedUser, getNotificationAlerts } from '@store/selectors/user.selector';
import { MessageService } from '@services/message.service';
import { NotificationService } from '@services/notification.service';
import { Loader } from '@store/actions/config.action';

@Component({
    selector: 'app-detalhe',
    templateUrl: './detalhe.component.html',
    styleUrls: ['./detalhe.component.scss'],
    standalone: false
})
export class DetalheComponent implements OnInit, OnDestroy {

  detailId: number;
  isNavBarOpened: boolean;
  isLoggedIn: boolean;
  marketNS = '';

  signal = false;
  chartSelected = true;

  newSignalingForm: UntypedFormGroup;
  submit = false;
  numberOnly = '^[0-9]*$';

  market: MarketDetails;
  public markets: Markets[] = [];
  marketCountry = '';
  marketCountriesName = [];
  marketCountriesLink = [];
  marketName = '';

  minValue = '';
  maxValue = '';

  args: any;
  fromPage = '/markets';

  private unsubscribe$: Subject<void> = new Subject();

  public marketDetails: MarketDetails[] = [];
  user: User;
  userFlag: UserFlag;

  constructor(private formBuilder: UntypedFormBuilder,
              public store: Store<AppState>,
              private messageService: MessageService,
              private router: Router,
              private readonly activatedRoute: ActivatedRoute,
              private readonly injector: Injector
              ) {
    this.marketNS = this.activatedRoute.snapshot.params.id;

    this.activatedRoute.queryParams.subscribe(params => {
      params.from !== undefined ? this.fromPage = '/home' : this.fromPage = '/markets';
    });

    this.store.pipe(select(getLoggedUser), takeUntil(this.unsubscribe$)).subscribe((user) => {
      if (user) {
        this.user = user;
        this.userFlag = user.flag;
        this.isLoggedIn = true;
      }
    });

    this.store.pipe(select(getMarketDetails), takeUntil(this.unsubscribe$)).subscribe(marketDetails => {
      if (marketDetails) {
        const marketHistory =  marketDetails.filter(x => x.ns === this.marketNS)[0];
        if (marketHistory.sct !== null) {
          if (marketHistory.sc === 'OMIE') {
            this.configCountries(marketHistory);
          }

          this.marketName = marketHistory.nm;
        }

        this.market = marketHistory;
      }
    });

    this.store.pipe(select(getMarkets), takeUntil(this.unsubscribe$)).subscribe(markets => {
      if (markets) {
        this.markets = markets;
      }
    });

    this.readNotifications();
  }

  ngOnInit() {
    this.newSignalingForm = this.formBuilder.group({
      signalValue: ['', [Validators.required, Validators.pattern(this.numberOnly),
      Validators.min(0), Validators.max(100)]]
    });
  }

  private configCountries(marketHistory: MarketDetails): void {
    const keys = Object.keys(marketHistory.sct);
    const values = Object.values(marketHistory.sct);

    if (keys.indexOf('PT') > -1 && this.marketCountriesName.indexOf('PORTUGAL') === -1) {
      this.marketCountriesName.push('PORTUGAL');
      this.marketCountriesLink.push(values[keys.indexOf('PT')]);
    }
    if (keys.indexOf('ES') > -1 && this.marketCountriesName.indexOf('ESPANHA') === -1) {
      this.marketCountriesName.push('ESPANHA');
      this.marketCountriesLink.push(values[keys.indexOf('ES')]);
    }
    if (keys.indexOf('MS') > -1 && this.marketCountriesName.indexOf('MARKET SPLITTING') === -1) {
      this.marketCountriesName.push('MARKET SPLITTING');
      this.marketCountriesLink.push(values[keys.indexOf('MS')]);
    }

    if (marketHistory.ct === 'ES') {
      this.marketCountry = 'ESPANHA';
    }
    if (marketHistory.ct === 'PT') {
      this.marketCountry = 'PORTUGAL';
    }
  }

  setMinValue(min) {
    this.minValue = min;
  }

  setMaxValue(max) {
    this.maxValue = max;
  }

  getMessage(message: boolean) {
    this.isNavBarOpened = message;
  }

  getLoggedIn(message: boolean) {
    this.isLoggedIn = message;
  }

  getChart(message: boolean) {
    this.chartSelected = message;
  }

  private readNotifications() {
    this.store.pipe(
      select(getNotificationAlerts),
      take(1)
    ).subscribe(alert => {
      const alertList = alert.filter(alertData => alertData.mns === this.marketNS);

      if (alertList && alertList.length) {
        const notificationService = this.injector.get(NotificationService);
        alertList.forEach(alertData => {
          if (alertData.notifications && alertData.notifications.length) {
            alertData.notifications.forEach(notification => notificationService.read(notification).subscribe());
          }
        });

        this.store.dispatch(new GetNotification());
      }
    });
  }

  get f() { return this.newSignalingForm.controls; }

  onSubmit() {
    if (this.newSignalingForm.invalid) {
        return;
    }
    this.submit = true;

    let currentValue: number;
    let maturity = '';

    for (const market of this.markets) {
      if (market.ns === this.marketNS) {
        currentValue = market.vl;
        if (market.mt !== '') {
          maturity = market.mt;
        }
      }
    }

    const mtval: number = currentValue + ((this.newSignalingForm.value.signalValue / 100) * currentValue);

    if (maturity !== '') {
      this.args = { mns: this.marketNS,
        mtv: mtval, mmt: maturity, mcv: currentValue };
    } else {
      this.args = { mns: this.marketNS,
        mtv: mtval, mcv: currentValue };
    }
  }

  confirm(): void {
    const sinalizationService = this.injector.get(SinalizationService);

    this.store.dispatch(new Loader(true));
    sinalizationService.createSinalization(this.args).subscribe((response) => {
      const translateService = this.injector.get(TranslateService);

      if (response.status === 200 && response.success) {
        this.store.dispatch(new GetSinalization());
        this.messageService.show(translateService.instant('edp.emarket.fast.quote.sinalizacao.enviada'));
        this.router.navigate(['/signalings']);
      } else {
        this.messageService.show(translateService.instant('edp.emarket.detalhe.erro.enviar.sinalizacao'), 'error');
      }

      this.store.dispatch(new Loader(false));
    });
  }

  cancel(): void {
    this.newSignalingForm.reset();
    this.signal = false;
    this.submit = false;
  }

  setSignal(): void {
    this.signal = true;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
